































































import Vue from 'vue';
import { Fragment } from 'vue-fragment';
import scrollIntoView from 'scroll-into-view-if-needed';

import questionnaire from '../data/questionnaire.json';
import apiFetch from '../lib/api';

import Bubble from './Bubble.vue';
import Button from './Button.vue';
import Form from './Form.vue';
import Mark from './Mark.vue';
import Title from './Title.vue';

type Answer = {
  text: string;
  correct: boolean;
};
type QuizItem = {
  question: string;
  answer: string;
  options: Answer[];
};
type Quiz = {
  title: string;
  subtitle: string;
  quiz: QuizItem[];
};
type Data = {
  answers: Answer[];
  content: Quiz;
  displayForm: boolean;
  displayOptions: boolean;
  points: number[];
  question: number;
  id: string | null;
  submitting: boolean;
};
type StartQuiz = {
  uuid: string;
};

export default Vue.extend({
  name: 'Questionnaire',
  components: {
    Bubble,
    Button,
    Form,
    Fragment,
    Mark,
    Title,
  },
  data(): Data {
    return {
      answers: [],
      content: questionnaire[process.env.VUE_APP_PHASE],
      displayForm: false,
      displayOptions: false,
      points: [],
      question: 0,
      id: null,
      submitting: false,
    };
  },
  mounted() {
    this.content.quiz = this.content.quiz.map(item => ({
      ...item,
      options: this.shuffleArray(item.options),
    }));

    let el = this.$refs.questionnaire as Element;

    if (el) {
      const observer = new MutationObserver(list => {
        const block = this.displayForm ? 'start' : 'end';

        if (this.displayForm) {
          const addedItems = list.find(({ addedNodes }) => addedNodes.length);

          if (addedItems) {
            el = addedItems.addedNodes[0] as Element;
          }
        }

        scrollIntoView(el, {
          scrollMode: 'if-needed',
          block,
        });
      });

      observer.observe(el, {
        attributes: false,
        childList: true,
        subtree: true,
      });
    }
  },
  computed: {
    activeQuiz(): QuizItem[] {
      return this.id
        ? this.content.quiz.filter((item: QuizItem, i: number) => i <= this.question)
        : [];
    },
    pointsList(): number[] {
      const questions = this.content.quiz.length;
      const total = 100;
      const fraction = Math.floor(total / questions);
      const remainder = total - fraction * questions;

      return this.content.quiz.map((item: QuizItem, i: number) => {
        if (i < questions - 1) {
          return fraction;
        }

        return fraction + remainder;
      });
    },
    pointsTotal(): number {
      return this.points.reduce((accu, curr) => accu + curr, 0);
    },
    lastQuestion(): boolean {
      return this.content.quiz.length <= this.question;
    },
  },
  methods: {
    getAnimationDelay(delay: number): string {
      return `${delay}s`;
    },
    getCorrectAnswer(index: number): string {
      const correct: Answer | undefined = this.content.quiz[index]
        .options.find(item => item.correct);

      if (correct) {
        return correct.text;
      }

      return '';
    },
    isCorrect(index: number): boolean {
      return this.points[index] > 0;
    },
    selectAnswer(answer: Answer): void {
      const points = answer.correct ? this.pointsList[this.question] : 0;

      this.answers.push(answer);
      this.points.push(points);

      this.question += 1;
      this.displayOptions = false;

      if (this.lastQuestion) {
        this.endQuiz();
      }
    },
    setDisplayForm() {
      setTimeout(() => {
        this.displayForm = true;
      }, 3500);
    },
    shuffleArray(array: any[]) {
      const inputArray: any[] = array;
      for (let i: number = inputArray.length - 1; i >= 0; i -= 1) {
        const randomIndex: number = Math.floor(Math.random() * (i + 1));
        const itemAtIndex: number = inputArray[randomIndex];

        inputArray[randomIndex] = inputArray[i];
        inputArray[i] = itemAtIndex;
      }
      return inputArray;
    },
    async endQuiz(): Promise<boolean> {
      try {
        await apiFetch(`end/${this.id}`);

        return true;
      } catch (response) {
        throw new Error(response);
      }
    },
    async startQuiz(): Promise<boolean> {
      try {
        this.submitting = true;

        const data: StartQuiz = await apiFetch('start');

        this.id = data.uuid;
        this.submitting = false;

        return true;
      } catch (response) {
        this.submitting = false;
        throw new Error(response);
      }
    },
  },
});
